import { HTMLAttributes } from 'react'
import { default as NextLink } from 'next/link'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Link from '@/components/base/link'
import Media, { MediaElement } from '@/components/base/media'
import MediaParallax from '@/components/base/media-parallax'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'

import { doublePushRevealAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPImage, WPLink } from '@/types'

export type DoublePushProps = HTMLAttributes<HTMLElement> &
  Partial<{
    items: {
      image: WPImage
      title: string
      paragraph: string
      link: WPLink
      media: MediaElement
    }[]
  }>

const DoublePush = ({ items, ...props }: DoublePushProps) => {
  return (
    <Section {...props}>
      <Container>
        <Grid className="gap-y-[3.2rem]">
          {items?.map(({ title, paragraph, link, media }, index) => (
            <div
              key={`item-${index}`}
              className={cn(
                'col-span-4 md:col-span-3 lg:col-span-6',
                'grid gap-y-[3.2rem]'
              )}
            >
              <MediaParallax className="relative aspect-[605/450] bg-havana">
                {(mediaRef) => (
                  <Media ref={mediaRef} fill fit="cover" {...media} />
                )}
              </MediaParallax>

              <Motion animation={doublePushRevealAnimation}>
                {(motionRef) => (
                  <div ref={motionRef} className="grid gap-y-[3.2rem]">
                    <div className="grid gap-y-[1.6rem]">
                      {title ? (
                        <Typography
                          variant="h5"
                          component="h4"
                          className="font-timesnow"
                          data-motion-title
                        >
                          {title}
                        </Typography>
                      ) : null}

                      {paragraph ? (
                        <Typography
                          variant="body"
                          paragraph
                          className="font-gillsans font-light"
                          data-motion-paragraph
                        >
                          {paragraph}
                        </Typography>
                      ) : null}
                    </div>

                    {link ? (
                      <div className="flex">
                        <Link asChild data-motion-link>
                          <NextLink href={link.url} target={link.target}>
                            <Typography variant="label">
                              {link.title}
                            </Typography>
                          </NextLink>
                        </Link>
                      </div>
                    ) : null}
                  </div>
                )}
              </Motion>
            </div>
          ))}
        </Grid>
      </Container>
    </Section>
  )
}

export default DoublePush
